import { AiDraftItem } from "@/pages/Private/pages/Messages/components/GenerateBar/types";

import { SenderRecipientOption } from "../components/SenderRecipientDropdown";

import {
	Draft,
	DraftRecipient,
	DraftSender,
	createDefaultDraft,
	timingFromDays,
} from "./inboxDraft.types";

import { aiItemIsInvitation } from "./pendingInvitation.util";
import { pickMyOption } from "./resolveMyDefaultSender.util";

import type { User } from "@/pages/Public/pages/Login/schema/login";

interface RebuildDraftsForChannelSwitchParams {
	aiItems: AiDraftItem[];
	targetChannel: "email" | "linkedin";
	/** Which side of the draft the user just edited (and should be kept as-is). */
	pinnedSide: "from" | "to";
	pinnedOption: SenderRecipientOption;
	senderOptions: SenderRecipientOption[];
	recipientOptions: SenderRecipientOption[];
	/** The current user's sending entry: a switched draft takes my own option (PREC-4312). */
	user: User | null | undefined;
	startingDraftId: number;
	/** Senders whose own invitation to the lead is pending: their note is no invitation (PREC-4216). */
	pendingInvitations?: Map<number, string | null>;
}

export interface RebuildDraftsForChannelSwitchResult {
	drafts: Draft[];
	nextDraftId: number;
}

/**
 * Re-build the drafts array from stored AI items when the user manually
 * switches the channel of a draft (by changing either the sender or recipient).
 *
 * Returns `null` when there are no AI items for the requested channel, so the
 * caller can fall back to a regular single-draft update.
 */
export const rebuildDraftsForChannelSwitch = ({
	aiItems,
	targetChannel,
	pinnedSide,
	pinnedOption,
	senderOptions,
	recipientOptions,
	user,
	startingDraftId,
	pendingInvitations,
}: RebuildDraftsForChannelSwitchParams): RebuildDraftsForChannelSwitchResult | null => {
	const filteredItems = aiItems.filter((item) => item.channel === targetChannel);

	if (filteredItems.length === 0) {
		return null;
	}

	const matchingSender =
		pinnedSide === "from"
			? null
			: (pickMyOption(senderOptions, targetChannel, user ?? undefined) ?? null);

	const matchingRecipient =
		pinnedSide === "to" ? null : (recipientOptions.find((r) => r.type === targetChannel) ?? null);

	let nextDraftId = startingDraftId;

	const drafts: Draft[] = filteredItems.map((item, idx) => {
		const fromSender: DraftSender =
			pinnedSide === "from"
				? {
						userId: pinnedOption.userId ?? user?.id ?? 0,
						type: targetChannel,
						label: pinnedOption.label,
						emailId: pinnedOption.emailId,
						emailAddress: pinnedOption.emailAddress,
					}
				: matchingSender
					? {
							userId: matchingSender.userId ?? user?.id ?? 0,
							type: targetChannel,
							label: matchingSender.label,
							emailId: matchingSender.emailId,
							emailAddress: matchingSender.emailAddress,
						}
					: {
							userId: user?.id ?? 0,
							type: targetChannel,
							label: "Sender",
						};

		const toRecipient: DraftRecipient =
			pinnedSide === "to"
				? {
						type: targetChannel,
						label: pinnedOption.label,
						value: pinnedOption.value ?? "",
					}
				: matchingRecipient
					? {
							type: targetChannel,
							label: matchingRecipient.label,
							value: matchingRecipient.value ?? "",
						}
					: {
							type: targetChannel,
							label: "Recipient",
							value: "",
						};

		const id = nextDraftId++;

		const draft = createDefaultDraft(
			id,
			fromSender,
			toRecipient,
			item.content,
			timingFromDays(item.scheduledInDays ?? (idx === 0 ? 0 : idx * 3))
		);

		if (targetChannel === "email" && item.subject) {
			draft.subject = item.subject;
		}

		// A sender whose own invitation is pending cannot send a second one (PREC-4216).
		if (aiItemIsInvitation(item, fromSender.userId, pendingInvitations)) {
			draft.isInviteNote = true;
		}

		if (item.aiOrigin) {
			draft.aiOrigin = item.aiOrigin;
		}

		return draft;
	});

	return { drafts, nextDraftId };
};
